'use client'

import { useEffect, useMemo, useState } from 'react'
import type { ColDef, ColGroupDef, GridApi, IRowNode } from 'ag-grid-community'
import type { CustomStatusPanelProps } from 'ag-grid-react'
import { useLocale, useTranslations } from 'next-intl'
import { z } from 'zod/v4'

import { formatPropertyNumericDisplayValue } from '@/services/property/property-display'

type QuantitySum = Record<string, number>

type MetricDisplayMetadata = {
  suffix?: string
  autoWeightUnit?: boolean
}

type MetricWithUnit = {
  value: number
  unit?: string
  fieldKey?: string
  displayMetadata?: MetricDisplayMetadata
}

type AdditionalMetrics = Record<string, MetricWithUnit>

export type CountStatusBarParams = {
  quantityNumberKey?: string
  quantityUnitKey?: string
  weightFieldKey?: string
  totalWeightFieldKey?: string
  trackedFieldKeys?: string[]
  excludeRow?: (node: IRowNode<unknown>) => boolean
}

type CountStatusBarProps = CustomStatusPanelProps & CountStatusBarParams

const DEFAULT_QUANTITY_NUMBER_KEY = 'quantity.number'
const DEFAULT_QUANTITY_UNIT_KEY = 'quantity.unit'
const DEFAULT_WEIGHT_FIELD_KEY = 'weight'
const DEFAULT_TRACKED_FIELD_KEYS = ['surface', 'volume']

const UNIT_CATEGORIES = {
  unitQuantities: ['unite', 'unité', 'piece', 'pièce', 'pce', 'unit', 'u'],
  linear: ['mm', 'cm', 'dm', 'm', 'km'],
  surface: ['mm²', 'mm2', 'cm²', 'cm2', 'dm²', 'dm2', 'm²', 'm2', 'km²', 'km2'],
  volume: ['mm³', 'mm3', 'cm³', 'cm3', 'dm³', 'dm3', 'm³', 'm3', 'km³', 'km3'],
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const parseQty = (qty: unknown): number => {
  if (typeof qty === 'number') return Number.isFinite(qty) ? qty : 0
  if (typeof qty !== 'string') return 0
  return parseFloat(qty.replace(/\s/g, '').replace(',', '.')) || 0
}

const normalizeUnit = (unit: unknown): string => {
  if (typeof unit !== 'string') return 'Sans unité'
  const trimmedUnit = unit.trim()
  if (!trimmedUnit || z.uuid().safeParse(trimmedUnit).success) {
    return 'Sans unité'
  }

  const replacements: Record<string, string> = {
    mm2: 'mm²',
    cm2: 'cm²',
    dm2: 'dm²',
    m2: 'm²',
    km2: 'km²',
    mm3: 'mm³',
    cm3: 'cm³',
    dm3: 'dm³',
    m3: 'm³',
    km3: 'km³',
  }

  let result = trimmedUnit
  for (const [pattern, replacement] of Object.entries(replacements)) {
    result = result.replace(new RegExp(pattern, 'gi'), replacement)
  }
  return result
}

const getByKeyOrPath = (
  data: unknown,
  keyOrPath: string,
  preferProperties = false,
): unknown => {
  if (!isRecord(data)) return undefined
  const property =
    preferProperties && Array.isArray(data.properties)
      ? data.properties.find(
          (entry) => isRecord(entry) && entry.key === keyOrPath,
        )
      : undefined
  if (isRecord(property)) return property.values
  if (Object.prototype.hasOwnProperty.call(data, keyOrPath)) {
    return data[keyOrPath]
  }
  if (!keyOrPath.includes('.')) return data[keyOrPath]

  return keyOrPath.split('.').reduce<unknown>((acc, segment) => {
    if (!isRecord(acc)) return undefined
    return acc[segment]
  }, data)
}

const toNumericValue = (value: unknown): number => {
  if (typeof value === 'number' || typeof value === 'string') {
    return parseQty(value)
  }
  if (Array.isArray(value) && value.length > 0) {
    return toNumericValue(value[0])
  }
  if (!isRecord(value)) return 0
  if (Object.prototype.hasOwnProperty.call(value, 'value')) {
    return toNumericValue(value.value)
  }
  if (Object.prototype.hasOwnProperty.call(value, 'number')) {
    return toNumericValue(value.number)
  }
  return 0
}

const toUnitLabel = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (!isRecord(value)) return ''

  if (typeof value.label === 'string' && value.label.trim().length > 0) {
    return value.label
  }
  if (typeof value.value === 'string' && value.value.trim().length > 0) {
    return value.value
  }
  if (Object.prototype.hasOwnProperty.call(value, 'unit')) {
    return toUnitLabel(value.unit)
  }

  return ''
}

const isWeightUnit = (unit: unknown): boolean => {
  if (typeof unit !== 'string') return false

  const unitLower = unit.toLowerCase().trim()
  const weightUnits = [
    'g',
    'kg',
    't',
    'tonne',
    'gramme',
    'kilogramme',
    'gram',
    'kilogram',
    'kilo',
  ]
  return weightUnits.some((weightUnit) => {
    return (
      unitLower === weightUnit ||
      unitLower.startsWith(weightUnit + ' ') ||
      unitLower.endsWith(' ' + weightUnit) ||
      unitLower.includes(' ' + weightUnit + ' ')
    )
  })
}

const convertToKg = (value: number, unit: unknown): number => {
  if (typeof unit !== 'string') return value

  const unitLower = unit.toLowerCase().trim()
  if (unitLower.includes('tonne') || unitLower === 't') return value * 1000
  if (unitLower.includes('kg') || unitLower.includes('kilo')) return value
  if (
    unitLower.includes('gramme') ||
    unitLower.includes('gram') ||
    unitLower === 'g'
  ) {
    return value / 1000
  }
  return value
}

const getUnitCategory = (unit: string): { category: number; order: number } => {
  const unitLower = unit.toLowerCase().trim()

  const unitQuantityIndex = UNIT_CATEGORIES.unitQuantities.findIndex(
    (pattern) => unitLower.includes(pattern),
  )
  if (unitQuantityIndex !== -1) {
    return { category: 1, order: unitQuantityIndex }
  }

  const linearIndex = UNIT_CATEGORIES.linear.findIndex(
    (linearUnit) =>
      unitLower === linearUnit ||
      (linearUnit === 'm' &&
        (unitLower === 'm' || unitLower === 'm ') &&
        !unitLower.includes('m²') &&
        !unitLower.includes('m³')),
  )
  if (linearIndex !== -1) {
    return { category: 2, order: linearIndex }
  }

  const surfaceIndex = UNIT_CATEGORIES.surface.findIndex((surfaceUnit) =>
    unitLower.includes(surfaceUnit),
  )
  if (surfaceIndex !== -1) {
    return { category: 3, order: Math.floor(surfaceIndex / 2) }
  }

  const volumeIndex = UNIT_CATEGORIES.volume.findIndex((volumeUnit) =>
    unitLower.includes(volumeUnit),
  )
  if (volumeIndex !== -1) {
    return { category: 4, order: Math.floor(volumeIndex / 2) }
  }

  return { category: 5, order: 0 }
}

const sortQuantities = (entries: [string, number][]): [string, number][] => {
  return entries.sort(([unitA], [unitB]) => {
    const categoryA = getUnitCategory(unitA)
    const categoryB = getUnitCategory(unitB)

    if (categoryA.category !== categoryB.category) {
      return categoryA.category - categoryB.category
    }
    return categoryA.order - categoryB.order
  })
}

const isColGroupDef = (col: ColDef | ColGroupDef): col is ColGroupDef =>
  Array.isArray((col as ColGroupDef).children)

const flattenColumnDefs = (colDefs: Array<ColDef | ColGroupDef>): ColDef[] => {
  const flat: ColDef[] = []
  for (const colDef of colDefs) {
    if (isColGroupDef(colDef)) {
      flat.push(...flattenColumnDefs(colDef.children))
      continue
    }
    flat.push(colDef)
  }
  return flat
}

const getColumnDisplayMetadata = (
  column: ColDef,
): MetricDisplayMetadata | null => {
  const { cellEditorParams } = column
  if (!isRecord(cellEditorParams)) return null

  const property = cellEditorParams.property
  if (!isRecord(property)) return null

  const suffix =
    typeof property.suffix === 'string' && property.suffix.trim().length > 0
      ? property.suffix
      : undefined
  const autoWeightUnit = property.autoWeightUnit === true

  if (!suffix && !autoWeightUnit) return null

  return {
    suffix,
    autoWeightUnit,
  }
}

const getFieldDisplayMetadata = (
  columnDefs: ColDef[],
  fieldKey: string,
): MetricDisplayMetadata | null => {
  const column = columnDefs.find((col) => col.field === fieldKey)
  if (!column) return null
  return getColumnDisplayMetadata(column)
}

type MetricsConfig = {
  quantityNumberKey: string
  quantityUnitKey: string
  weightFieldKey: string
  totalWeightFieldKey?: string
  fieldsToTrack: string[]
}

const calculateMetrics = (
  nodes: IRowNode<unknown>[],
  columnDefs: ColDef[],
  config: MetricsConfig,
): {
  quantitySum: QuantitySum
  additionalMetrics: AdditionalMetrics
} => {
  const quantitySum: QuantitySum = {}
  const metrics: AdditionalMetrics = {}

  for (const key of config.fieldsToTrack) {
    const displayMetadata = getFieldDisplayMetadata(columnDefs, key)
    if (displayMetadata) {
      metrics[key] = { value: 0, fieldKey: key, displayMetadata }
    }
  }

  let totalWeightKg = 0
  const trackedMetrics = Object.entries(metrics)
  const normalizedUnits = new Map<string, string>()

  for (const { data } of nodes) {
    if (!data) continue

    const quantity = toNumericValue(
      getByKeyOrPath(data, config.quantityNumberKey),
    )
    const quantityUnitLabel = toUnitLabel(
      getByKeyOrPath(data, config.quantityUnitKey),
    )
    let unit = normalizedUnits.get(quantityUnitLabel)
    if (unit === undefined) {
      unit = normalizeUnit(quantityUnitLabel)
      normalizedUnits.set(quantityUnitLabel, unit)
    }
    quantitySum[unit] = (quantitySum[unit] || 0) + quantity

    if (config.totalWeightFieldKey) {
      totalWeightKg += toNumericValue(
        getByKeyOrPath(data, config.totalWeightFieldKey, true),
      )
    } else if (isWeightUnit(quantityUnitLabel)) {
      totalWeightKg += convertToKg(quantity, quantityUnitLabel)
    } else {
      const unitWeight = toNumericValue(
        getByKeyOrPath(data, config.weightFieldKey),
      )
      if (unitWeight > 0) {
        totalWeightKg += quantity * unitWeight
      }
    }

    for (const [key, metric] of trackedMetrics) {
      metric.value += toNumericValue(
        getByKeyOrPath(data, metric.fieldKey ?? key),
      )
    }
  }

  if (totalWeightKg > 0) {
    metrics.weight = {
      value: totalWeightKg,
      displayMetadata: { autoWeightUnit: true },
    }
  }

  return { quantitySum, additionalMetrics: metrics }
}

const defaultExcludeRow = (node: IRowNode<unknown>): boolean => {
  if (node.group) return true
  return typeof node.id === 'string' && node.id.includes('new')
}

const collectFilteredNodes = (
  api: GridApi,
  excludeRow: (node: IRowNode<unknown>) => boolean,
): IRowNode<unknown>[] => {
  const nodes: IRowNode<unknown>[] = []
  api.forEachNodeAfterFilter((node) => {
    if (!excludeRow(node) && node.data) {
      nodes.push(node)
    }
  })
  return nodes
}

const collectAllNodes = (
  api: GridApi,
  excludeRow: (node: IRowNode<unknown>) => boolean,
): IRowNode<unknown>[] => {
  const nodes: IRowNode<unknown>[] = []
  api.forEachNode((node) => {
    if (!excludeRow(node) && node.data) {
      nodes.push(node)
    }
  })
  return nodes
}

export const CountStatusBar = (props: CountStatusBarProps) => {
  const [totalRows, setTotalRows] = useState(0)
  const [displayedRows, setDisplayedRows] = useState(0)
  const [selectedRows, setSelectedRows] = useState(0)
  const [quantitySum, setQuantitySum] = useState<QuantitySum>({})
  const [additionalMetrics, setAdditionalMetrics] = useState<AdditionalMetrics>(
    {},
  )
  const t = useTranslations()
  const locale = useLocale()

  const quantityNumberKey =
    props.quantityNumberKey ?? DEFAULT_QUANTITY_NUMBER_KEY
  const quantityUnitKey = props.quantityUnitKey ?? DEFAULT_QUANTITY_UNIT_KEY
  const weightFieldKey = props.weightFieldKey ?? DEFAULT_WEIGHT_FIELD_KEY
  const totalWeightFieldKey = props.totalWeightFieldKey
  const fieldsToTrack = props.trackedFieldKeys ?? DEFAULT_TRACKED_FIELD_KEYS
  const excludeRow = props.excludeRow ?? defaultExcludeRow
  const contextEmptyRows = isRecord(props.context)
    ? props.context.emptyRows
    : null

  useEffect(() => {
    let filteredNodes: IRowNode<unknown>[] = []
    let visibleNodes = new Set<IRowNode<unknown>>()
    let rowsInvalidated = true
    let frameId: number | null = null
    let timerId: ReturnType<typeof setTimeout> | null = null

    const updateMetrics = () => {
      frameId = null
      timerId = null
      if (props.api.isDestroyed()) return
      if (rowsInvalidated) {
        filteredNodes = collectFilteredNodes(props.api, excludeRow)
        visibleNodes = new Set(filteredNodes)
        setDisplayedRows(filteredNodes.length)
        setTotalRows(collectAllNodes(props.api, excludeRow).length)
        rowsInvalidated = false
      }

      const selectedNodes = (props.api.getSelectedNodes() ?? []).filter(
        (node) => visibleNodes.has(node),
      )
      setSelectedRows(selectedNodes.length)
      const columnDefs = flattenColumnDefs(props.api.getColumnDefs() ?? [])
      const nextMetrics = calculateMetrics(
        selectedNodes.length > 0 ? selectedNodes : filteredNodes,
        columnDefs,
        {
          quantityNumberKey,
          quantityUnitKey,
          weightFieldKey,
          totalWeightFieldKey,
          fieldsToTrack,
        },
      )
      setQuantitySum(nextMetrics.quantitySum)
      setAdditionalMetrics(nextMetrics.additionalMetrics)
    }

    const scheduleUpdate = () => {
      if (frameId !== null || timerId !== null) return
      // Paste, fill and group edits emit several events for the same final
      // model. Read it once per frame, after the whole batch has settled.
      if (typeof requestAnimationFrame === 'function') {
        frameId = requestAnimationFrame(updateMetrics)
      } else {
        timerId = setTimeout(updateMetrics, 0)
      }
    }

    const listener: Parameters<GridApi['addGlobalListener']>[0] = (
      _eventType,
      event,
    ) => {
      if (props.api.isDestroyed()) return

      switch (event.type as string) {
        case 'selectionChanged':
          scheduleUpdate()
          break
        case 'cellValueChanged':
        case 'rowValueChanged':
        case 'rowNodeDataChanged':
          // Nodes retain their identity during cell edits. A custom exclusion
          // predicate may depend on the changed value and needs a fresh set.
          rowsInvalidated ||= excludeRow !== defaultExcludeRow
          scheduleUpdate()
          break
        case 'filterChanged':
        case 'modelUpdated':
        case 'rowDataUpdated':
          rowsInvalidated = true
          scheduleUpdate()
          break
        default:
          break
      }
    }

    props.api.addGlobalListener(listener)
    updateMetrics()

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId)
      if (timerId !== null) clearTimeout(timerId)
      if (!props.api.isDestroyed()) {
        props.api.removeGlobalListener(listener)
      }
    }
  }, [
    contextEmptyRows,
    excludeRow,
    fieldsToTrack,
    props.api,
    quantityNumberKey,
    quantityUnitKey,
    totalWeightFieldKey,
    weightFieldKey,
  ])

  const allQuantities = useMemo(
    () =>
      sortQuantities(
        Object.entries(quantitySum).filter(
          ([unit, sum]) =>
            sum > 0 && unit !== 'Sans unité' && !isWeightUnit(unit),
        ),
      ),
    [quantitySum],
  )

  const unitCountQuantities = useMemo(
    () =>
      allQuantities.filter(([unit]) => getUnitCategory(unit).category === 1),
    [allQuantities],
  )

  const metricQuantities = useMemo(
    () =>
      allQuantities.filter(([unit]) => {
        const category = getUnitCategory(unit).category
        return category >= 2 && category <= 4
      }),
    [allQuantities],
  )

  const exoticQuantities = useMemo(
    () =>
      allQuantities.filter(([unit]) => getUnitCategory(unit).category === 5),
    [allQuantities],
  )

  const sortedMetrics = useMemo(() => {
    const metricsOrder = Array.from(new Set([...fieldsToTrack, 'weight']))
    return metricsOrder
      .filter(
        (key) => additionalMetrics[key] && additionalMetrics[key].value > 0,
      )
      .map((key) => ({ key, metric: additionalMetrics[key] }))
  }, [additionalMetrics, fieldsToTrack])

  const isFiltered = displayedRows < totalRows

  return (
    <div className="flex h-10 items-center gap-x-2 divide-x divide-neutral-300">
      <p>
        {isFiltered ? (
          <span className="component">
            {t('Grid.filteredRows', {
              displayed: displayedRows,
              total: totalRows,
            })}
          </span>
        ) : (
          <>
            <span className="component">{t('Grid.total')} </span>
            <span className="ag-status-name-value-value">{totalRows}</span>
          </>
        )}
      </p>
      {selectedRows > 0 && (
        <p className="pl-2">
          <span className="component">{t('Grid.selected')} </span>
          <span className="ag-status-name-value-value">
            {selectedRows} / {displayedRows}
          </span>
        </p>
      )}
      {unitCountQuantities.map(([unit, sum]) => (
        <MetricDisplay key={unit} value={sum} unit={unit} locale={locale} />
      ))}
      {metricQuantities.map(([unit, sum]) => (
        <MetricDisplay key={unit} value={sum} unit={unit} locale={locale} />
      ))}
      {sortedMetrics.map(({ key, metric }) => (
        <MetricDisplay
          key={key}
          value={metric.value}
          unit={metric.unit}
          displayMetadata={metric.displayMetadata}
          isPrice={key === 'price'}
          locale={locale}
        />
      ))}
      {exoticQuantities.map(([unit, sum]) => (
        <MetricDisplay key={unit} value={sum} unit={unit} locale={locale} />
      ))}
    </div>
  )
}

const MetricDisplay = ({
  value,
  unit,
  displayMetadata,
  isPrice = false,
  locale,
}: {
  value: number
  unit?: string
  displayMetadata?: MetricDisplayMetadata
  isPrice?: boolean
  locale: string
}) => {
  const formattedValue = displayMetadata
    ? formatPropertyNumericDisplayValue({
        value,
        locale,
        suffix: displayMetadata.suffix,
        autoWeightUnit: displayMetadata.autoWeightUnit,
        minimumFractionDigits: isPrice ? 2 : 0,
        maximumFractionDigits: isPrice ? 2 : 2,
      })
    : `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: isPrice ? 2 : 0,
        maximumFractionDigits: isPrice ? 2 : 2,
      }).format(value)}${unit ? ` ${unit}` : ''}`

  return (
    <p className="pl-2">
      <span className="ag-status-name-value-value">{formattedValue}</span>
    </p>
  )
}
